import React, { useState, useEffect } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Avatar,
    Paper,
    Divider,
    Button,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useService } from 'contexts/ServiceContextProvider';
import { useAuthorization } from 'contexts/AuthContextProvider';
import { useWorkspaces } from 'contexts/WorkspacesContextProvider';
import { MentionsInput, Mention } from 'react-mentions';
import { useTranslation } from "react-i18next";
import "../../css/components/Core/TaskComments.css";
import "../../css/main.css";
import TinyCustomFallback from 'components/Custom/TinyCustomFallback';
import { DateTime } from 'luxon';
import SkeletonComment from '../Custom/Skeletons/CommentSkeleton';


const renderCommentWithMentions = (text) => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;

    return text.replace(mentionRegex, (match, display, id) => {
        return `<a href="/workflow/profile/${id}">@${display}</a>`;
    });
};

export default function Comments({ task_id, project_id, myUserTimezone, taskComments, hasMoreComments, setHasMoreComments, updateCommentsState }) {
    const { t } = useTranslation();

    const { PostTaskComment, fetchMoreComments, fetchMoreReplies } = useService();
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [replyComment, setReplyComment] = useState({});
    const { getLoggedUser } = useAuthorization();
    const [showReplyBoxForCommentId, setShowReplyBoxForCommentId] = useState(null); // Track reply box for a specific comment

    const [avoidMisfire, setAvoidMisfire] = useState(false);

    const workspaceContext = useWorkspaces();
    const myWorkspaces = workspaceContext.workspaces;
    const workspaceWorkers = workspaceContext.workspaceWorkers;

    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const [isLoadingReplies, setLoadingReplies] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        setComments(taskComments);
        setIsLoading(false);
    }, [taskComments]);


    const handleLoadMoreComments = async () => {
        setIsLoadingComments(true);
        try {
            const { data } = await fetchMoreComments(myWorkspaces[0].id, project_id, task_id);
            const newComments = data.filter(
                (newComment) => !comments.some((existingComment) => existingComment.id === newComment.id)
            );
            setComments((prevComments) => [...newComments, ...prevComments]);
            setHasMoreComments(false);
        } catch (error) {
            console.error('Error loading more comments:', error);
        } finally {
            setIsLoadingComments(false);
        }
    };

    const handleLoadMoreReplies = async (commentId) => {
        setLoadingReplies((prev) => ({ ...prev, [commentId]: true }));
        try {
            const { data: replies, has_more } = await fetchMoreReplies(myWorkspaces[0].id, project_id, task_id, commentId);

            setComments((prevComments) =>
                prevComments.map((comment) => {
                    if (comment.id === commentId) {
                        const newReplies = replies.filter(
                            (newReply) => !comment.replies.some((existingReply) => existingReply.id === newReply.id)
                        );

                        return {
                            ...comment,
                            replies: [...newReplies, ...comment.replies],
                            has_more_replies: has_more
                        };
                    }
                    return comment;
                })
            );
        } catch (error) {
            console.error('Error loading more replies:', error);
        } finally {
            setLoadingReplies((prev) => ({ ...prev, [commentId]: false }));
        }
    };

    const extractMentionedIds = (text) => {
        const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
        const mentionedIds = [];
        let match;

        while ((match = mentionRegex.exec(text)) !== null) {
            mentionedIds.push(match[2]);
        }

        return mentionedIds;
    };

    const handlePostComment = async () => {
        if (newComment.trim() === '') return;
        if (!avoidMisfire) {
            setAvoidMisfire(true);

            updateCommentsState(true)

            const mentionedIds = extractMentionedIds(newComment);

            try {
                const commentData = {
                    text: newComment,
                    parent_comment: null, // For top-level comments
                    user_id: getLoggedUser(),
                    mentioned_users: mentionedIds,
                };
                const postedComment = await PostTaskComment(myWorkspaces[0].id, project_id, task_id, commentData);

                // Prepend the new comment to the list
                setComments((prevComments) => [postedComment, ...prevComments]);
                setNewComment(''); // Clear the input after posting
            } catch (error) {
                console.error('Error posting comment:', error);
            } finally{
                setTimeout(() => {
                    setAvoidMisfire(false);
                  }, 1000);
            }
        }
    };

    const handlePostReply = async (parentId) => {
        const replyText = replyComment[parentId]?.trim();
        if (!replyText) return;

        const mentionedIds = extractMentionedIds(replyText);

        try {
            const replyData = {
                text: replyText,
                parent_comment: parentId, // Set the parent_comment ID for replies
                user_id: getLoggedUser(),
                mentioned_users: mentionedIds,
            };
            const postedReply = await PostTaskComment(myWorkspaces[0].id, project_id, task_id, replyData);

            const updatedComments = comments.map((comment) => {
                if (comment.id === parentId) {
                    return {
                        ...comment,
                        replies: [...comment.replies, postedReply] // Add the new reply to the list
                    };
                }
                return comment;
            });

            setComments(updatedComments);
            setReplyComment({ ...replyComment, [parentId]: '' });
            setShowReplyBoxForCommentId(null); // Hide reply box after posting the reply
        } catch (error) {
            console.error('Error posting reply:', error);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handlePostComment();
        }
    };

    const handleReplyKeyPress = (event, parentId) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handlePostReply(parentId);
        }
    };

    const mentionData = workspaceWorkers.map(worker => ({
        id: worker.id,
        display: worker.full_name,
        image: worker.image,
    }));

    const handleShowReplyBox = (commentId) => {
        setShowReplyBoxForCommentId(commentId); // Show reply box for the selected comment
    };

    if (isLoading) {
        return <SkeletonComment />;
    }
    return (
        <Box className={`comments-margin ${!taskComments?.length && newComment === "" ? "opacity-box" : ""}`}>
            <Typography variant="h6">{t("modal.comments_title")}</Typography>
            <Divider className="comments-divider" />

            {isLoadingComments ? (
                <>
                    <Button>{t("modal.loading_comments")} <TinyCustomFallback /> </Button>
                </>
            ) : (
                hasMoreComments && (
                    <Button onClick={handleLoadMoreComments}>{t("modal.load_more_comments")}</Button>
                )
            )}

            {comments.length > 0 ? (
                comments.map((comment) => (
                    <Box key={comment.id} className="comments-box">
                        <Comment
                            comment={comment}
                            onReply={(text) =>
                                setReplyComment({ ...replyComment, [comment.id]: text })
                            }
                            replyText={replyComment[comment.id] || ''}
                            onPostReply={() => handlePostReply(comment.id)}
                            onReplyKeyPress={(e) => handleReplyKeyPress(e, comment.id)}
                            mentionData={mentionData}
                            showReplyBox={showReplyBoxForCommentId === comment.id} // Conditionally show reply box
                            onShowReplyBox={() => handleShowReplyBox(comment.id)} // Handle reply box toggle
                            t={t}
                            myUserTimezone={myUserTimezone}
                            handleLoadMoreReplies={handleLoadMoreReplies}
                            isLoadingReplies={isLoadingReplies[comment.id]}
                        />
                        <Divider />

                    </Box>

                ))
            ) : (
                <Typography>{t("modal.no_comments")}</Typography>
            )}

            <Typography variant="h6">{t("modal.leave_comment")}</Typography>
            <Paper className="comments-paper">
                <MentionsInput
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="mentions"
                    onKeyPress={handleKeyPress}
                    placeholder={t("modal.write_comments")}
                >
                    <Mention
                        trigger="@"
                        data={mentionData}
                        className="comments-mention-input"
                        renderSuggestion={(suggestion, search, highlightedDisplay) => (
                            <Box className="comments-flex">
                                <Avatar
                                    src={`${suggestion.image}`}
                                    className="comments-avatar"
                                />
                                <Typography variant="body2">{highlightedDisplay}</Typography>
                            </Box>
                        )}
                        displayTransform={(id, display) => `@${display}`}
                    />
                </MentionsInput>
                <IconButton
                    className="comments-icon-button"
                    onClick={handlePostComment}
                    disabled={avoidMisfire}
                >
                    <SendIcon className={avoidMisfire ? "black-txt-color" : "white-txt-color"} />
                </IconButton>
            </Paper>
        </Box>
    );
}


const Comment = ({ comment, onReply, replyText, onPostReply, onReplyKeyPress, mentionData, showReplyBox, onShowReplyBox, t, myUserTimezone, handleLoadMoreReplies, isLoadingReplies }) => (
    <Box className="comments-comment-padding">
        <Box className="comments-flex">
            <Avatar src={`${comment.user.image}`} className="comments-avatar-margin">
                {comment.user?.full_name ? comment.user.full_name[0] : comment.user?.username?.[0]}
            </Avatar>
            <Box>
                <Typography variant="subtitle1">
                    {comment.user.full_name} - {
                        DateTime.fromISO(comment.created_at, { zone: 'utc' })
                            .setZone(myUserTimezone)
                            .toFormat('dd/MM/yyyy HH:mm')
                    }
                </Typography>
                <Typography
                    variant="body2"
                    className="comments-comment-text"
                    dangerouslySetInnerHTML={{ __html: renderCommentWithMentions(comment.text) }}
                />
            </Box>
        </Box>
        
        {comment.has_more_replies && (
            isLoadingReplies ? (
                <Box>
                    <Button disabled>{t("modal.loading_replies")}</Button>
                    <TinyCustomFallback />
                </Box>
            ) : (
                <Box className="comments-reply-button">
                    <Button className="comments-reply-button" onClick={() => handleLoadMoreReplies(comment.id)}>
                        {t("modal.load_more_replies")}
                    </Button>
                </Box>
            )
        )}
        
        {comment.replies && comment.replies.length > 0 && (
            <Box className="comments-reply-button">
                {comment.replies.map((reply) => (
                    <Box key={reply.id} className="comments-reply-padding">
                        <Box className="comments-flex">
                            <Avatar src={`${reply.user.image}`} className="comments-avatar-margin">{reply.user.full_name[0]}</Avatar>
                            <Box>
                                <Typography variant="subtitle2">
                                    {reply.user.full_name} - {
                                        DateTime.fromISO(reply.created_at, { zone: 'utc' })
                                            .setZone(myUserTimezone)
                                            .toFormat('dd/MM/yyyy HH:mm')
                                    }
                                </Typography>
                                <Typography
                                    variant="body2"
                                    className="comments-comment-text"
                                    dangerouslySetInnerHTML={{ __html: renderCommentWithMentions(reply.text) }}
                                />
                            </Box>
                        </Box>
                    </Box>
                ))}


            </Box>

        )}

        {/* Conditionally render the reply button if showReplyBox is false */}
        {!showReplyBox && (
        <Box className="comments-reply-button">
            <Button onClick={onShowReplyBox} variant="text" className="button-dark-bg">
                {t("core.reply")}
            </Button>
        </Box>)}

        {/* Conditionally render the reply input field if showReplyBox is true */}
        {showReplyBox && (
            <Box className="comments-reply-box">
                <MentionsInput
                    value={replyText}
                    onChange={(e) => onReply(e.target.value)}
                    className="mentions"
                    onKeyPress={onReplyKeyPress}
                    placeholder={t("core.reply_placeholder")}
                >
                    <Mention
                        trigger="@"
                        data={mentionData}
                        className="comments-mention-input"
                        renderSuggestion={(suggestion, search, highlightedDisplay) => (
                            <Box className="comments-flex">
                                <Avatar
                                    src={`${suggestion.image}`}
                                    className="comments-avatar"
                                />
                                <Typography variant="body2">{highlightedDisplay}</Typography>
                            </Box>
                        )}
                        displayTransform={(id, display) => `@${display}`}
                    />
                </MentionsInput>
                <IconButton
                    className="comments-icon-button"
                    onClick={onPostReply}
                >
                    <SendIcon className="white-txt-color" />
                </IconButton>
            </Box>
        )}
    </Box>
);
